import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 * Thermite 10/28: the video waits until the page has loaded and the video is on screen,
 * is skipped on data-saver / 2g / 3g connections (the preview image stays), prefers the
 * 720p source on wide screens, and fades in once it is actually playing.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const connection = /** @type {any} */ (navigator).connection;
    if (connection && (connection.saveData || /(^|-)2g$|3g/.test(connection.effectiveType || ''))) return;

    const whenVisible = () => {
      if (!('IntersectionObserver' in window)) return this.startVideo();
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            this.startVideo();
          }
        },
        { rootMargin: '200px' }
      );
      observer.observe(this);
    };

    if (document.readyState === 'complete') whenVisible();
    else window.addEventListener('load', whenVisible, { once: true });
  }

  startVideo() {
    const { videoSources, videoElement } = this.refs;
    let sources = [...videoSources];

    if (window.innerWidth >= 750) {
      const hd = sources.find((source) => /720p/.test(source.dataset.videoSource || ''));
      if (hd) sources = [hd, ...sources.filter((source) => source !== hd)];
      for (const source of sources) videoElement.appendChild(source);
    }

    for (const source of sources) {
      const { videoSource } = source.dataset;
      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.addEventListener('playing', () => this.classList.add('is-playing'), { once: true });
    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
